import { ContentPageSectionVisibilityEnum } from '@/services/configurator/PlannerConfigTypes';
import { ContentPageVariant } from '@pnpmworkspace/shared-types';

export type PhraseCategory = {
  text: string;
  description?: string;
  phraseKeys: string[];
  key: CategorizedPhraseKey;
};

export const CATEGORIZED_PHRASE_KEYS = [
  'keywords',
  'banner_small',
  'banner_large',
  'cta',
  'video',
  'page-meta',
  'page-intro',
  'page-primary-banner',
  'page-secondary-banner',
  'page-primary-card',
  'page-secondary-card',
] as const;

export type CategorizedPhraseKey = (typeof CATEGORIZED_PHRASE_KEYS)[number];

export type PhraseCategoryMap = Record<CategorizedPhraseKey, PhraseCategory>;

export function isCategorizedPhraseKey(
  value?: string,
): value is CategorizedPhraseKey {
  if (value) {
    return CATEGORIZED_PHRASE_KEYS.includes(value as CategorizedPhraseKey);
  }
  return false;
}

export function toCategorizedPhraseKey(
  value: ContentPageSectionVisibilityEnum,
): CategorizedPhraseKey | undefined {
  switch (value) {
    case 'pagePrimaryCard':
      return 'page-primary-card';
    case 'pageSecondaryBanner':
      return 'page-secondary-banner';
    case 'pageSecondaryCard':
      return 'page-secondary-card';
    case 'video':
      return 'video';
  }
}

export function toContentPageSectionVisibility(
  value: CategorizedPhraseKey,
): ContentPageSectionVisibilityEnum | undefined {
  switch (value) {
    case 'page-primary-card':
      return 'pagePrimaryCard';
    case 'page-secondary-banner':
      return 'pageSecondaryBanner';
    case 'page-secondary-card':
      return 'pageSecondaryCard';
    case 'video':
      return 'video';
  }
}

type GetPhraseCategoriesParams = {
  contentPageVariant?: ContentPageVariant;
  component: string;
};

export type PhraseKeysCategory = keyof PhraseCategoryMap;

const BaseLineContentPageTranslations: Pick<
  PhraseCategoryMap,
  'page-meta' | 'page-intro'
> = {
  'page-meta': {
    text: 'Page Meta',
    key: 'page-meta',
    description: 'SEO meta data for content page',
    phraseKeys: ['page-meta-title', 'page-meta-description'],
  },
  'page-intro': {
    text: 'Page Intro',
    description: 'Text and description rendered at the top of the content page',
    key: 'page-intro',
    phraseKeys: ['page-intro-title', 'page-intro-description'],
  },
};

const BaseLineBannerTranslations: Pick<
  PhraseCategoryMap,
  | 'page-primary-banner'
  | 'page-secondary-banner'
  | 'page-primary-card'
  | 'page-secondary-card'
> = {
  'page-primary-banner': {
    text: 'Primary Banner',
    description:
      'Primary banner, uses the imageUrl from configuration page or overriden by market overrides',
    key: 'page-primary-banner',
    phraseKeys: [
      'page-primary-banner-title',
      'page-primary-banner-description',
      'page-primary-banner-cta',
      'page-primary-banner-alt-text',
    ],
  },
  'page-secondary-banner': {
    text: 'Secondary Banner',
    key: 'page-secondary-banner',
    description:
      'Secondary banner, uses the secondaryImageUrl from configuration page or overriden by market overrides',
    phraseKeys: [
      'page-secondary-banner-title',
      'page-secondary-banner-description',
      'page-secondary-banner-cta',
      'page-secondary-banner-img-alt-text',
    ],
  },
  'page-primary-card': {
    text: 'Primary Card',
    key: 'page-primary-card',
    description:
      'Optional field, will use the primaryCardCtaUrl if provided in market config (This is just a placeholder example)',
    phraseKeys: [
      'page-primary-card-title',
      'page-primary-card-description',
      'page-primary-card-cta',
    ],
  },
  'page-secondary-card': {
    text: 'Secondary Card',
    key: 'page-secondary-card',
    description:
      'Optional field, will use the secondaryCardCtaUrl if provided in market config (This is just a placeholder example)',
    phraseKeys: [
      'page-secondary-card-title',
      'page-secondary-card-description',
      'page-secondary-card-cta',
    ],
  },
};

const InjectCurrentPage: Partial<PhraseCategoryMap> = {
  ...BaseLineContentPageTranslations,
};

const BannersWithLink: Partial<PhraseCategoryMap> = {
  ...BaseLineContentPageTranslations,
  ...BaseLineBannerTranslations,
};

const BannersWithVideoAndLink: Partial<PhraseCategoryMap> = {
  ...BaseLineContentPageTranslations,
  ...BaseLineBannerTranslations,
  video: {
    text: 'Video',
    description:
      'This is rendered conditionally if videoUrl is provided in market config (This is just a placeholder example)',
    phraseKeys: ['videoTitle', 'videoDescription'],
    key: 'video',
  },
};

export const getContentPageComponents = (
  contentPageVariant?: ContentPageVariant,
): Partial<PhraseCategoryMap> => {
  if (contentPageVariant === 'banners_with_external_link') {
    return {
      ...BannersWithLink,
    };
  }
  if (contentPageVariant === 'banners_with_video_and_external_link') {
    return {
      ...BannersWithVideoAndLink,
    };
  }
  if (contentPageVariant === 'inject_current_page') {
    return {
      ...InjectCurrentPage,
    };
  }
  return {};
};

export const categorizedPhraseKeys: PhraseCategoryMap = {
  ...BaseLineContentPageTranslations,
  ...BaseLineBannerTranslations,
  keywords: {
    text: 'Search Keywords',
    description:
      'These keywords will make the planner show up when a user types them in the search box on IKEA.com.',
    phraseKeys: ['keywords'],
    key: 'keywords',
  },
  banner_small: {
    text: 'PIP Buy module & Banner Fields',
    description: 'These fields are used for the small banner on PIP pages.',
    key: 'banner_small',
    phraseKeys: ['small-title', 'small-description'],
  },
  banner_large: {
    text: 'PIP & PLP Banner Fields',
    description: 'These fields are used for the large banner in PIP and PLP.',
    key: 'banner_large',
    phraseKeys: [
      'medium-title',
      'medium-description',
      'large-title',
      'large-description',
      'type',
    ],
  },
  cta: {
    text: 'Call To Action',
    description:
      'This field is used for both the PIP and PLP banner cta button.',
    key: 'cta',
    phraseKeys: ['cta'],
  },
  video: {
    text: 'Video',
    description:
      'This is rendered conditionally if videoUrl is provided in market config (This is just a placeholder example)',
    phraseKeys: ['videoTitle', 'videoDescription'],
    key: 'video',
  },
};

export const getAllPhraseKeys = () => {
  return Object.fromEntries(
    Object.values(categorizedPhraseKeys)
      .flatMap((category) => category.phraseKeys)
      .map((x) => [x, '']),
  );
};

export const getPhraseCategory = ({
  component,
}: GetPhraseCategoriesParams): PhraseCategory => {
  if (isCategorizedPhraseKey(component)) {
    return categorizedPhraseKeys[component];
  }
  throw new Error(
    `Could not get phrase categories for componenent '${component}'`,
  );
};
